import PropTypes from "prop-types";
import { Swiper, SwiperSlide } from "swiper/react";
import { useCurrentFormatCurrency } from "@hooks/usePrices";
import useDeviceDetect from "@hooks/useDeviceDetect";
import { cn } from "@helpers/general";
import { resizeShopifyImage } from "@helpers/product";

function GiftProduct({
  product,
  onlyOneAvailableGift,
  tagLabel,
  textColor,
  noOfProducts,
  isAvailable,
}) {
  const formatWithCurrency = useCurrentFormatCurrency();
  const { isMobile } = useDeviceDetect();

  if (!isMobile) {
    return (
      <div className="relative group flex-1">
        <img
          src={resizeShopifyImage(product.shopify.images[0].url, "x400")}
          alt={product.title}
          className={cn("aspect-square object-cover group-hover:opacity-0", {
            "w-[100px] h-[100px]": onlyOneAvailableGift,
          })}
        />
        <img
          src={resizeShopifyImage(product.shopify.images[1].url, "x400")}
          alt={product.title}
          className={cn(
            "aspect-square object-cover opacity-0 absolute group-hover:opacity-100 top-0",
            { "w-[100px] h-[100px]": onlyOneAvailableGift }
          )}
        />
        <span
          className={cn(
            "typo-micro font-bold text-center uppercase whitespace-nowrap absolute -top-3 py-[3px] px-[7px] bg-white border rounded-[4px] left-1/2 transform -translate-x-1/2 z-[1]",
            {
              "px-[5px]": noOfProducts > 3,
            }
          )}
          style={{ color: textColor, borderColor: textColor }}
        >
          {isAvailable
            ? `${formatWithCurrency(product?.prices?.fullPrice)} ${tagLabel}`
            : "Sold Out"}
        </span>
      </div>
    );
  }

  return (
    <div
      className={cn("overflow-hidden flex-1 relative text-center", {
        "overflow-visible": onlyOneAvailableGift,
      })}
    >
      <span
        className={cn(
          "typo-body-bold text-center uppercase whitespace-nowrap relative top-[8px] z-[2] py-[3px] px-[7px] bg-white border rounded-[4px]",
          { "absolute -top-[10px] left-1/2 -translate-x-1/2": onlyOneAvailableGift }
        )}
        style={{ color: textColor, borderColor: textColor }}
      >
        {isAvailable ? `${formatWithCurrency(product?.prices?.fullPrice)} ${tagLabel}` : "Sold Out"}
      </span>
      <Swiper
        spaceBetween={0}
        slidesPerView={1}
        loop
        pagination={{
          clickable: true,
          bulletClass: "al-bullet",
          bulletActiveClass: "al-bullet-active",
        }}
        className={cn("min-w-0", { "w-[100px] h-[100px]": onlyOneAvailableGift })}
      >
        {product.shopify.images.slice(0, 2).map((image) => (
          <SwiperSlide key={`gift-product-${image.url}`}>
            <img
              src={resizeShopifyImage(image.url, "x400")}
              alt={product.title}
              className="flex aspect-square object-cover w-full block"
            />
          </SwiperSlide>
        ))}
      </Swiper>
    </div>
  );
}

GiftProduct.propTypes = {
  onlyOneAvailableGift: PropTypes.bool,
  tagLabel: PropTypes.string,
  textColor: PropTypes.string,
  noOfProducts: PropTypes.number,
  isAvailable: PropTypes.bool,
  product: PropTypes.shape({
    handle: PropTypes.string.isRequired,
    title: PropTypes.string.isRequired,
    shopify: PropTypes.shape({
      images: PropTypes.arrayOf(
        PropTypes.shape({
          url: PropTypes.string.isRequired,
        })
      ).isRequired,
      tags: PropTypes.arrayOf(PropTypes.string).isRequired,
    }).isRequired,
    prices: PropTypes.shape({
      fullPrice: PropTypes.number,
    }),
    fullPrice: PropTypes.number.isRequired,
  }),
};

export default GiftProduct;
